import React from "react";
import {
  InlineTimePicker,
  InlineDatePicker,
  MuiPickersUtilsProvider,
} from "material-ui-pickers";
import DateFnsUtils from "@date-io/date-fns";
import { isValid as isDateTimeValid, parse, format } from "date-fns";
import { withStyles } from "@material-ui/core/styles";
import DateRange from "@material-ui/icons/DateRange";
import ArrowLeft from "@material-ui/icons/ChevronLeft";
import ArrowRight from "@material-ui/icons/ChevronRight";
import Time from "@material-ui/icons/AccessTime";
import Clear from "@material-ui/icons/CancelOutlined";
import InputAdornment from "@material-ui/core/InputAdornment";

const styles = (theme) => ({
  dateTimeContainer: {
    maxWidth: 160,
    backgroundColor: "transparent",
    marginTop: 6,
    "& $inputRoot $inputStyle$textInput": {
      fontSize: theme.textFontSize,
      color: theme.TextColor,
      padding: 5,
      height: 24,
      border: 0,
      "&:focus": {
        padding: 5,
        border: 0,
      },
      "&::placeholder": {
        color: "inherit",
      },
    },
  },
  paperStyle: {
    backgroundColor: theme.ModalBGColor,
    color: theme.TextColor,
  },
  labelProps: {
    fontSize: theme.textFontSize,
    color: theme.TextColor,
    opacity: 0.4,
  },
  inputStyle: {
    fontSize: theme.textFontSize,
    color: theme.TextColor,
    padding: 5,
    height: 24,
    border: 0,
    "&:focus": {
      padding: 5,
      border: 0,
    },
  },
  textInput: {},
  inputRoot: {
    fontSize: theme.textFontSize,
    color: theme.TextColor,
    backgroundColor: theme.ComponentHeader,
    outline: "none",
    height: 24,
    border: `1px solid ${theme.iconColor}`,
    boxSizing: "border-box",
    "&::before, &::after": {
      border: "none !important",
    },
    "&$disabledInput": {
      backgroundColor: theme.disabledTextBG,
    },
  },
  inputError: {
    border: `1px solid ${theme.errorBorderColor}`,
  },
  iconColor: {
    color: theme.iconColor,
  },
  inputAdornment: {
    cursor: "pointer",
  },
  iconStyle: {
    color: theme.iconColor,
    cursor: "pointer",
    paddingRight: 4,
  },
  dateTimeWidth: {
    maxWidth: 130,
  },
  dateWidth: {
    maxWidth: 130,
  },
  timeWidth: {
    maxWidth: 100,
  },
  helperText: {
    margin: 0,
    paddingTop: 4,
    fontSize: 11,
    color: theme.errorBorderColor,
  },
  disabledInput: {},
});

class TimeComponent extends React.Component {
  clear = () => {
    if (!this.props.disabled) {
      this.onChange(null);
    }
  };

  onInputChange = (event) => {
    // On keyboard input, update the datetime value when valid value is added
    // updating invalid value to datetime will not allow you to edit again
    const updatedDateTime = parse(
      event.target.value,
      this.props.dateType.format,
      new Date()
    );
    if (isDateTimeValid(updatedDateTime)) {
      this.onChange(updatedDateTime);
    } else if (this.props.onChange) {
      this.props.onChange(updatedDateTime);
    }
  };

  onChange = (value) => {
    if (typeof value === "string" && value) {
      value = new Date(value);
    }
    this.updateParent(value);
  };

  updateParent = (value) => {
    if (this.props.onChange) {
      let dateTime = null;
      if (value && isDateTimeValid(value)) {
        dateTime = format(value, this.props.dateType.updateFormat);
      }
      this.props.onChange(dateTime);
    }
  };

  openPicker = (event) => {
    if (!this.props.disabled) {
      this.picker.open(event);
    }
  };

  getPickerTypeData = () => {
    return this.props.dateType.type === "Time"
      ? {
          PickerComponent: InlineTimePicker,
          KeyboardIcon: Time,
          inputWidthClass: this.props.classes.timeWidth,
          iconProps: {
            ampm: false,
          },
        }
      : {
          PickerComponent: InlineDatePicker,
          KeyboardIcon: DateRange,
          inputWidthClass: this.props.classes.dateWidth,
          iconProps: {
            leftArrowIcon: <ArrowLeft />,
            rightArrowIcon: <ArrowRight />,
          },
        };
  };

  render() {
    const { classes, disableFocusInput, dateType, value, id } = this.props;
    const disablePickerPortal = !Boolean(this.props.enablePickerPortal);
    const { PickerComponent, KeyboardIcon, inputWidthClass, iconProps } =
      this.getPickerTypeData();
    return (
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <div className={classes.dateTimeContainer}>
          <PickerComponent
            id={id}
            keyboard
            clearable
            disableOpenOnEnter
            format={dateType.format}
            placeholder={dateType.placeholder}
            {...iconProps}
            value={value ? parse(value, dateType.format, new Date()) : value}
            onChange={this.onChange}
            mask={dateType.inputMask}
            ref={(node) => (this.picker = node)}
            FormHelperTextProps={{
              classes: {
                root: classes.helperText,
              },
            }}
            PopoverProps={{
              disablePortal: disablePickerPortal,
            }}
            InputLabelProps={{
              classes: {
                root: classes.labelProps,
              },
            }}
            classes={{
              popoverPaper: classes.paperStyle,
            }}
            InputProps={{
              autoFocus: disableFocusInput ? false : true,
              endAdornment: (
                <InputAdornment
                  position="end"
                  classes={{ root: classes.inputAdornment }}
                >
                  {value ? (
                    <Clear
                      classes={{ root: classes.iconStyle }}
                      onClick={this.clear}
                    />
                  ) : null}
                  <KeyboardIcon
                    classes={{ root: classes.iconStyle }}
                    onClick={this.openPicker}
                  />
                </InputAdornment>
              ),
              classes: {
                root: `dateTimeContainer ${
                  classes.inputRoot
                } ${inputWidthClass} ${
                  this.props.errorText ? classes.inputError : ""
                }`,
                input: `${classes.inputStyle} ${classes.textInput}`,
                disabled: classes.disabledInput,
              },
              inputRef: this.props.inputRef,
            }}
            shouldDisableDate={this.props?.shouldDisableDate || false}
            disabled={this.props.disabled ?? false}
            onInputChange={this.onInputChange}
            disableFuture={this.props.disableFuture || false}
            disablePast={this.props.disablePast || false}
            invalidDateMessage={`Invalid ${dateType.type}`}
            onKeyDown={this.props?.onKeyDown}
            {...(this.props.errorText
              ? { error: true, helperText: this.props.errorText }
              : {})}
            minDate={this.props.minDate}
            maxDate={this.props.maxDate}
            minDateMessage={this.props.minDateMessage}
            maxDateMessage={this.props.maxDateMessage}
          />
        </div>
      </MuiPickersUtilsProvider>
    );
  }
}
export default withStyles(styles)(TimeComponent);
